import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  AddCircleOutline as AddSubpromptIcon,
  ExpandMore as ChevronDownIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faComment, 
  faFile, 
  faChartBar, 
  faCalendar, 
  faBookmark, 
  faStickyNote,
  faAddressCard,
  faBuilding,
  faCircle,
  faClock,
  faClone,
  faCompass,
  faCopyright,
  faCreditCard,
  faFaceSmile,
  faFileAudio,
  faFileCode,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faFileZipper,
  faFolderOpen,
  faHdd,
  faHeart,
  faHourglass,
  faImage,
  faKeyboard,
  faLightbulb,
  faMap,
  faMoon,
  faNewspaper,
  faPaperPlane,
  faPaste,
  faPenToSquare,
  faRectangleList,
  faStar,
  faSun,
  faThumbsUp,
  faUser
} from '@fortawesome/free-regular-svg-icons';
import {
  faTag,
  faGlobe,
  faAnchor,
  faArrowUpRightDots,
  faBell,
  faBolt,
  faBrain,
  faBriefcase,
  faBullhorn,
  faCamera,
  faCartShopping,
  faChartPie,
  faChess,
  faCode,
  faCoffee,
  faCogs,
  faCoins,
  faDatabase,
  faDiagramProject,
  faEarthAmericas,
  faFire,
  faFlag,
  faFlask,
  faGaugeHigh,
  faGears,
  faGem,
  faGraduationCap,
  faHeadset,
  faHouse,
  faInfo,
  faLink,
  faMagnifyingGlass,
  faMapLocation,
  faMicrochip,
  faMicrophone,
  faMusic,
  faPalette,
  faPenRuler,
  faPeopleGroup,
  faPhone,
  faPrint,
  faPuzzlePiece,
  faRobot,
  faRocket,
  faScroll,
  faShield,
  faShoppingBag,
  faSignal,
  faSitemap,
  faStopwatch,
  faTableList,
  faTerminal,
  faToolbox,
  faTrophy,
  faUserGear,
  faVideo,
  faWifi
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '@clerk/clerk-react';
import { API_URL } from '../../config';
import { useAppDispatch } from '../../store/hooks';
import { SubPrompt } from '../../store/promptsSlice';
import { 
  createSubPrompt, 
  updateSubPrompt, 
  deleteSubPrompt as deleteSubPromptAction, 
  toggleSubPromptEnabled 
} from '../../store/promptsSlice';

// Define interfaces for prompts
interface Prompt {
  id: string;
  title: string;
  promptContent: string;
  enabled: boolean;
  isGlobal: boolean;
  iconType: string;
  createdAt: string;
  updatedAt: string;
  subPrompts?: SubPrompt[];
  users?: PromptAccessUser[];
}

interface PromptAccessUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  isDeactivated?: boolean;
  role?: string;
}

const getUserDisplayName = (user: PromptAccessUser) => {
  const firstName = user.firstName?.trim() ?? '';
  const lastName = user.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName.length > 0) {
    return fullName;
  }

  return user.email;
};

// Define available icons
const AVAILABLE_ICONS = [
  // Communication
  { value: 'email', label: 'Email', icon: <FontAwesomeIcon icon={faEnvelope} /> },
  { value: 'message', label: 'Message', icon: <FontAwesomeIcon icon={faComment} /> },
  { value: 'paper-plane', label: 'Send', icon: <FontAwesomeIcon icon={faPaperPlane} /> },
  { value: 'phone', label: 'Phone', icon: <FontAwesomeIcon icon={faPhone} /> },
  { value: 'bullhorn', label: 'Announcement', icon: <FontAwesomeIcon icon={faBullhorn} /> },
  { value: 'headset', label: 'Support', icon: <FontAwesomeIcon icon={faHeadset} /> },
  { value: 'microphone', label: 'Microphone', icon: <FontAwesomeIcon icon={faMicrophone} /> },
  { value: 'video', label: 'Video Call', icon: <FontAwesomeIcon icon={faVideo} /> },
  
  // Documents
  { value: 'document', label: 'Document', icon: <FontAwesomeIcon icon={faFile} /> },
  { value: 'file-pdf', label: 'PDF', icon: <FontAwesomeIcon icon={faFilePdf} /> },
  { value: 'file-word', label: 'Word', icon: <FontAwesomeIcon icon={faFileWord} /> },
  { value: 'file-excel', label: 'Excel', icon: <FontAwesomeIcon icon={faFileExcel} /> },
  { value: 'file-powerpoint', label: 'PowerPoint', icon: <FontAwesomeIcon icon={faFilePowerpoint} /> },
  { value: 'file-image', label: 'Image File', icon: <FontAwesomeIcon icon={faFileImage} /> },
  { value: 'file-audio', label: 'Audio File', icon: <FontAwesomeIcon icon={faFileAudio} /> },
  { value: 'file-video', label: 'Video File', icon: <FontAwesomeIcon icon={faFileVideo} /> },
  { value: 'file-code', label: 'Code File', icon: <FontAwesomeIcon icon={faFileCode} /> },
  { value: 'file-zipper', label: 'Archive', icon: <FontAwesomeIcon icon={faFileZipper} /> },
  { value: 'newspaper', label: 'News', icon: <FontAwesomeIcon icon={faNewspaper} /> },
  { value: 'folder', label: 'Folder', icon: <FontAwesomeIcon icon={faFolderOpen} /> },
  { value: 'clipboard', label: 'Clipboard', icon: <FontAwesomeIcon icon={faPaste} /> },
  { value: 'clone', label: 'Copy', icon: <FontAwesomeIcon icon={faClone} /> },
  
  // Analytics & Data
  { value: 'chart', label: 'Bar Chart', icon: <FontAwesomeIcon icon={faChartBar} /> },
  { value: 'chart-pie', label: 'Pie Chart', icon: <FontAwesomeIcon icon={faChartPie} /> },
  { value: 'signal', label: 'Metrics', icon: <FontAwesomeIcon icon={faSignal} /> },
  { value: 'gauge', label: 'Dashboard', icon: <FontAwesomeIcon icon={faGaugeHigh} /> },
  { value: 'database', label: 'Database', icon: <FontAwesomeIcon icon={faDatabase} /> },
  { value: 'table', label: 'Table', icon: <FontAwesomeIcon icon={faTableList} /> },
  { value: 'sitemap', label: 'Sitemap', icon: <FontAwesomeIcon icon={faSitemap} /> },
  { value: 'diagram', label: 'Diagram', icon: <FontAwesomeIcon icon={faDiagramProject} /> },
  { value: 'growth', label: 'Growth', icon: <FontAwesomeIcon icon={faArrowUpRightDots} /> },
  
  // Time & Planning
  { value: 'calendar', label: 'Calendar', icon: <FontAwesomeIcon icon={faCalendar} /> },
  { value: 'clock', label: 'Clock', icon: <FontAwesomeIcon icon={faClock} /> },
  { value: 'hourglass', label: 'Hourglass', icon: <FontAwesomeIcon icon={faHourglass} /> },
  { value: 'stopwatch', label: 'Stopwatch', icon: <FontAwesomeIcon icon={faStopwatch} /> },
  
  // Locations & Travel
  { value: 'globe', label: 'Globe', icon: <FontAwesomeIcon icon={faGlobe} /> },
  { value: 'earth', label: 'Earth', icon: <FontAwesomeIcon icon={faEarthAmericas} /> },
  { value: 'map', label: 'Map', icon: <FontAwesomeIcon icon={faMap} /> },
  { value: 'location', label: 'Location', icon: <FontAwesomeIcon icon={faMapLocation} /> },
  { value: 'compass', label: 'Compass', icon: <FontAwesomeIcon icon={faCompass} /> },
  { value: 'anchor', label: 'Anchor', icon: <FontAwesomeIcon icon={faAnchor} /> },
  
  // Business & Work
  { value: 'briefcase', label: 'Business', icon: <FontAwesomeIcon icon={faBriefcase} /> },
  { value: 'building', label: 'Company', icon: <FontAwesomeIcon icon={faBuilding} /> },
  { value: 'cogs', label: 'Settings', icon: <FontAwesomeIcon icon={faCogs} /> },
  { value: 'gears', label: 'Configuration', icon: <FontAwesomeIcon icon={faGears} /> },
  { value: 'toolbox', label: 'Tools', icon: <FontAwesomeIcon icon={faToolbox} /> },
  { value: 'address-card', label: 'Contact', icon: <FontAwesomeIcon icon={faAddressCard} /> },
  { value: 'user', label: 'User', icon: <FontAwesomeIcon icon={faUser} /> },
  { value: 'user-gear', label: 'User Settings', icon: <FontAwesomeIcon icon={faUserGear} /> },
  { value: 'people', label: 'Team', icon: <FontAwesomeIcon icon={faPeopleGroup} /> },
  { value: 'print', label: 'Print', icon: <FontAwesomeIcon icon={faPrint} /> },
  { value: 'credit-card', label: 'Payment', icon: <FontAwesomeIcon icon={faCreditCard} /> },
  { value: 'coins', label: 'Finance', icon: <FontAwesomeIcon icon={faCoins} /> },
  { value: 'shopping', label: 'Shopping', icon: <FontAwesomeIcon icon={faCartShopping} /> },
  { value: 'bag', label: 'Bag', icon: <FontAwesomeIcon icon={faShoppingBag} /> },
  
  // Creative & Content
  { value: 'lightbulb', label: 'Idea', icon: <FontAwesomeIcon icon={faLightbulb} /> },
  { value: 'brain', label: 'Intelligence', icon: <FontAwesomeIcon icon={faBrain} /> },
  { value: 'edit', label: 'Edit', icon: <FontAwesomeIcon icon={faPenToSquare} /> },
  { value: 'design', label: 'Design', icon: <FontAwesomeIcon icon={faPenRuler} /> },
  { value: 'image', label: 'Image', icon: <FontAwesomeIcon icon={faImage} /> },
  { value: 'camera', label: 'Camera', icon: <FontAwesomeIcon icon={faCamera} /> },
  { value: 'music', label: 'Music', icon: <FontAwesomeIcon icon={faMusic} /> },
  { value: 'palette', label: 'Creative', icon: <FontAwesomeIcon icon={faPalette} /> },
  { value: 'code', label: 'Code', icon: <FontAwesomeIcon icon={faCode} /> },
  { value: 'terminal', label: 'Terminal', icon: <FontAwesomeIcon icon={faTerminal} /> },
  
  // Markers & Statuses
  { value: 'bookmark', label: 'Bookmark', icon: <FontAwesomeIcon icon={faBookmark} /> },
  { value: 'tag', label: 'Tag', icon: <FontAwesomeIcon icon={faTag} /> },
  { value: 'note', label: 'Note', icon: <FontAwesomeIcon icon={faStickyNote} /> },
  { value: 'list', label: 'List', icon: <FontAwesomeIcon icon={faRectangleList} /> },
  { value: 'flag', label: 'Flag', icon: <FontAwesomeIcon icon={faFlag} /> },
  { value: 'star', label: 'Star', icon: <FontAwesomeIcon icon={faStar} /> },
  { value: 'heart', label: 'Heart', icon: <FontAwesomeIcon icon={faHeart} /> },
  { value: 'thumbs-up', label: 'Like', icon: <FontAwesomeIcon icon={faThumbsUp} /> },
  { value: 'circle', label: 'Circle', icon: <FontAwesomeIcon icon={faCircle} /> },
  { value: 'bell', label: 'Notification', icon: <FontAwesomeIcon icon={faBell} /> },
  { value: 'info', label: 'Info', icon: <FontAwesomeIcon icon={faInfo} /> },
  
  // Technology
  { value: 'robot', label: 'AI', icon: <FontAwesomeIcon icon={faRobot} /> },
  { value: 'rocket', label: 'Launch', icon: <FontAwesomeIcon icon={faRocket} /> },
  { value: 'bolt', label: 'Power', icon: <FontAwesomeIcon icon={faBolt} /> },
  { value: 'fire', label: 'Trending', icon: <FontAwesomeIcon icon={faFire} /> },
  { value: 'wifi', label: 'Wireless', icon: <FontAwesomeIcon icon={faWifi} /> },
  { value: 'hdd', label: 'Storage', icon: <FontAwesomeIcon icon={faHdd} /> },
  { value: 'microchip', label: 'Hardware', icon: <FontAwesomeIcon icon={faMicrochip} /> },
  { value: 'keyboard', label: 'Keyboard', icon: <FontAwesomeIcon icon={faKeyboard} /> },
  { value: 'link', label: 'Link', icon: <FontAwesomeIcon icon={faLink} /> },
  { value: 'house', label: 'Home', icon: <FontAwesomeIcon icon={faHouse} /> },
  
  // Miscellaneous
  { value: 'gem', label: 'Premium', icon: <FontAwesomeIcon icon={faGem} /> },
  { value: 'trophy', label: 'Achievement', icon: <FontAwesomeIcon icon={faTrophy} /> },
  { value: 'graduation', label: 'Education', icon: <FontAwesomeIcon icon={faGraduationCap} /> },
  { value: 'coffee', label: 'Coffee', icon: <FontAwesomeIcon icon={faCoffee} /> },
  { value: 'chess', label: 'Strategy', icon: <FontAwesomeIcon icon={faChess} /> },
  { value: 'puzzle', label: 'Puzzle', icon: <FontAwesomeIcon icon={faPuzzlePiece} /> },
  { value: 'shield', label: 'Security', icon: <FontAwesomeIcon icon={faShield} /> },
  { value: 'flask', label: 'Experiment', icon: <FontAwesomeIcon icon={faFlask} /> },
  { value: 'scroll', label: 'Scroll', icon: <FontAwesomeIcon icon={faScroll} /> },
  { value: 'smile', label: 'Smile', icon: <FontAwesomeIcon icon={faFaceSmile} /> },
  { value: 'sun', label: 'Sun', icon: <FontAwesomeIcon icon={faSun} /> },
  { value: 'moon', label: 'Moon', icon: <FontAwesomeIcon icon={faMoon} /> },
  { value: 'search', label: 'Search', icon: <FontAwesomeIcon icon={faMagnifyingGlass} /> },
  { value: 'copyright', label: 'Copyright', icon: <FontAwesomeIcon icon={faCopyright} /> }
].sort((a, b) => a.label.localeCompare(b.label));

// Sortable Prompt Item Component for drag and drop
interface SortablePromptItemProps {
  prompt: Prompt;
  expandedPromptId: string | null;
  onToggleExpand: (promptId: string) => void;
  onEditPrompt: (prompt: Prompt) => void;
  onDeletePrompt: (prompt: Prompt) => void;
  onToggleEnabled: (prompt: Prompt) => void;
  onOpenSubpromptDialog: (promptId: string) => void;
  onEditSubprompt: (subprompt: SubPrompt) => void;
  onDeleteSubprompt: (subprompt: SubPrompt) => void;
  onToggleSubpromptEnabled: (subprompt: SubPrompt) => void;
  getIconComponent: (iconType: string) => JSX.Element;
  onManageAccess: (prompt: Prompt) => void;
  isMobile: boolean;
}

const SortablePromptItem: React.FC<SortablePromptItemProps> = ({
  prompt,
  expandedPromptId,
  onToggleExpand,
  onEditPrompt,
  onDeletePrompt,
  onToggleEnabled,
  onOpenSubpromptDialog,
  onEditSubprompt,
  onDeleteSubprompt,
  onToggleSubpromptEnabled,
  getIconComponent,
  onManageAccess,
  isMobile
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: prompt.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const dragHandleProps = {
    ...attributes,
    ...listeners,
    className: 'dnd-mobile-handle',
    style: {
      cursor: 'grab',
      touchAction: 'none',
      userSelect: 'none' as const,
      WebkitUserSelect: 'none' as const,
    }
  };

  const assignedUsers = prompt.users ?? [];
  const maxVisibleUsers = isMobile ? 2 : 4;
  const visibleUsers = assignedUsers.slice(0, maxVisibleUsers);
  const hiddenUsersCount = assignedUsers.length - visibleUsers.length;

  if (isMobile) {
    // Mobile card view with improved touch support
    return (
      <div ref={setNodeRef} style={style}>
        <Card sx={{ 
          mb: 1, 
          borderRadius: 2, 
          border: isDragging ? '2px dashed #ccc' : 'none',
          boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.2)' : undefined,
          transform: isDragging ? 'rotate(5deg)' : 'none'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Box
                  {...dragHandleProps}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '44px',
                    minHeight: '44px',
                    border: '1px solid rgba(0,0,0,0.1)',
                    '&:active': { 
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      transform: 'scale(0.95)'
                    }
                  }}
                >
                  <DragIcon sx={{ color: 'rgba(0,0,0,0.6)' }} />
                </Box>
                <Box sx={{ minWidth: 30 }}>
                  {getIconComponent(prompt.iconType)}
                </Box>
                <Typography variant="body1" sx={{ flex: 1, fontWeight: 'medium' }}>
                  {prompt.title}
                </Typography>
              </Box>
              <Chip 
                label={prompt.enabled ? "Enabled" : "Disabled"} 
                size="small" 
                color={prompt.enabled ? "success" : "default"}
                icon={prompt.enabled ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Members with access
              </Typography>
              {assignedUsers.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                  {visibleUsers.map((user) => (
                    <Chip
                      key={user.id}
                      label={getUserDisplayName(user)}
                      size="small"
                    />
                  ))}
                  {hiddenUsersCount > 0 && (
                    <Chip
                      label={`+${hiddenUsersCount}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No users assigned
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => onManageAccess(prompt)}
                sx={{ mt: 1 }}
              >
                Manage access
              </Button>
            </Box>
            
            <Box sx={{ mt: 2, fontSize: '0.75rem', color: 'text.secondary' }}>
              <Typography variant="caption" component="div">
                Created: {new Date(prompt.createdAt).toLocaleDateString()}
              </Typography>
              {prompt.updatedAt && (
                <Typography variant="caption" component="div">
                  Updated: {new Date(prompt.updatedAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </CardContent>
          <Divider />
          
          {/* Subprompts Section */}
          {expandedPromptId === prompt.id && prompt.subPrompts && prompt.subPrompts.length > 0 && (
            <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sub-prompts
              </Typography>
              
              {prompt.subPrompts.map((subprompt) => (
                <Card key={subprompt.id} sx={{ mb: 1, backgroundColor: 'white' }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ minWidth: 20 }}>
                          {getIconComponent(subprompt.iconType || 'clipboard')}
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                          {subprompt.title}
                        </Typography>
                      </Box>
                      <Box>
                        <Switch
                          checked={subprompt.enabled}
                          onChange={() => onToggleSubpromptEnabled(subprompt)}
                          size="small"
                        />
                        <IconButton 
                          size="small" 
                          onClick={() => onEditSubprompt(subprompt)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => onDeleteSubprompt(subprompt)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      display: 'block',
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {subprompt.promptContent}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
              
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<AddSubpromptIcon />}
                onClick={() => onOpenSubpromptDialog(prompt.id)}
                sx={{ mt: 1 }}
              >
                Add Sub-prompt
              </Button>
            </Box>
          )}
          
          <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
            <Button
              size="small"
              startIcon={expandedPromptId === prompt.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => onToggleExpand(prompt.id)}
            >
              {expandedPromptId === prompt.id 
                ? `Hide Sub-prompts${prompt.subPrompts && prompt.subPrompts.length > 0 ? ` (${prompt.subPrompts.length})` : ''}`
                : `Show Sub-prompts${prompt.subPrompts && prompt.subPrompts.length > 0 ? ` (${prompt.subPrompts.length})` : ''}`
              }
            </Button>
            
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={prompt.enabled}
                    onChange={() => onToggleEnabled(prompt)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">{prompt.enabled ? "Enabled" : "Disabled"}</Typography>}
                sx={{ mr: 1 }}
              />
              <IconButton 
                size="small" 
                onClick={() => onEditPrompt(prompt)}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => onDeletePrompt(prompt)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardActions>
        </Card>
      </div>
    );
  }

  // Desktop table row view
  return (
    <React.Fragment>
      <TableRow hover ref={setNodeRef} style={style} sx={{ 
        border: isDragging ? '2px dashed #ccc' : 'none',
        boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.2)' : undefined,
      }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => onToggleExpand(prompt.id)}
            aria-label="expand row"
          >
            {expandedPromptId === prompt.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box
            {...dragHandleProps}
            sx={{
              p: 0.5,
              borderRadius: 1,
              backgroundColor: 'rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '32px',
              minHeight: '32px',
              border: '1px solid rgba(0,0,0,0.1)',
              '&:active': { 
                backgroundColor: 'rgba(0,0,0,0.1)',
                transform: 'scale(0.95)'
              }
            }}
          >
            <DragIcon sx={{ color: 'rgba(0,0,0,0.6)' }} />
          </Box>
        </TableCell>
        <TableCell width={50}>
          {getIconComponent(prompt.iconType)}
        </TableCell>
        <TableCell>
          <Typography variant="body1" fontWeight="medium">
            {prompt.title}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {assignedUsers.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {visibleUsers.map((user) => (
                  <Chip
                    key={user.id}
                    label={getUserDisplayName(user)}
                    size="small"
                    sx={{ maxWidth: 160 }}
                  />
                ))}
                {hiddenUsersCount > 0 && (
                  <Chip
                    label={`+${hiddenUsersCount}`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users assigned
              </Typography>
            )}
            <Button
              size="small"
              variant="text"
              onClick={() => onManageAccess(prompt)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0 }}
            >
              Manage access
            </Button>
          </Box>
        </TableCell>
        <TableCell>
          <FormControlLabel
            control={
              <Switch
                checked={prompt.enabled}
                onChange={() => onToggleEnabled(prompt)}
                size="small"
              />
            }
            label={prompt.enabled ? "Enabled" : "Disabled"}
          />
        </TableCell>
        <TableCell>
          {new Date(prompt.createdAt).toLocaleDateString()}
        </TableCell>
        <TableCell>
          {prompt.updatedAt
            ? new Date(prompt.updatedAt).toLocaleDateString()
            : '-'}
        </TableCell>
        <TableCell align="right">
          <IconButton
            onClick={() => onEditPrompt(prompt)}
            color="primary"
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => onDeletePrompt(prompt)}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      
      {/* Subprompts expanded row */}
      {expandedPromptId === prompt.id && (
        <TableRow>
          <TableCell colSpan={9} sx={{ py: 0, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1">
                  Sub-prompts
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<AddSubpromptIcon />}
                  onClick={() => onOpenSubpromptDialog(prompt.id)}
                >
                  Add Sub-prompt
                </Button>
              </Box>
              
              {prompt.subPrompts && prompt.subPrompts.length > 0 ? (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.1)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell>Icon</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Content</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {prompt.subPrompts.map((subprompt) => (
                        <TableRow key={subprompt.id} hover>
                          <TableCell width={40}>
                            {getIconComponent(subprompt.iconType || 'clipboard')}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {subprompt.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {subprompt.promptContent}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={subprompt.enabled}
                              onChange={() => onToggleSubpromptEnabled(subprompt)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              onClick={() => onEditSubprompt(subprompt)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => onDeleteSubprompt(subprompt)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No sub-prompts found. Add a sub-prompt to enhance this prompt's capabilities.
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

const PromptManagement: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [newPromptTitle, setNewPromptTitle] = useState('');
  const [newPromptContent, setNewPromptContent] = useState('');
  const [newPromptEnabled, setNewPromptEnabled] = useState(true);
  const [selectedIconType, setSelectedIconType] = useState('email');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [accessDialogLoading, setAccessDialogLoading] = useState(false);
  const [accessMutationLoading, setAccessMutationLoading] = useState(false);
  const [accessPrompt, setAccessPrompt] = useState<Prompt | null>(null);
  const [accessAssignedUsers, setAccessAssignedUsers] = useState<PromptAccessUser[]>([]);
  const [accessAvailableUsers, setAccessAvailableUsers] = useState<PromptAccessUser[]>([]);
  const { getToken } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const dispatch = useAppDispatch();
  
  // Add new state for expanded panels and subprompt dialog
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);
  const [subpromptDialogOpen, setSubpromptDialogOpen] = useState(false);
  const [editingSubprompt, setEditingSubprompt] = useState<SubPrompt | null>(null);
  const [newSubpromptTitle, setNewSubpromptTitle] = useState('');
  const [newSubpromptContent, setNewSubpromptContent] = useState('');
  const [newSubpromptEnabled, setNewSubpromptEnabled] = useState(true);
  const [selectedSubpromptIconType, setSelectedSubpromptIconType] = useState('clipboard');
  const [parentPromptId, setParentPromptId] = useState<string | null>(null);
  const [deleteSubpromptDialogOpen, setDeleteSubpromptDialogOpen] = useState(false);
  const [selectedSubprompt, setSelectedSubprompt] = useState<SubPrompt | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Drag and drop sensors with improved mobile support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 8,
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    // Prevent body scroll on mobile during drag
    if (isMobile) {
      document.body.classList.add('dragging');
    }
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    
    // Re-enable body scroll on mobile
    if (isMobile) {
      document.body.classList.remove('dragging');
    }

    if (over && active.id !== over.id) {
      const oldIndex = prompts.findIndex((prompt) => prompt.id === active.id);
      const newIndex = prompts.findIndex((prompt) => prompt.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newPrompts = arrayMove(prompts, oldIndex, newIndex);
        setPrompts(newPrompts);

        // Update the order in the backend
        try {
          const promptOrders = newPrompts.map((prompt, index) => ({
            id: prompt.id,
            order: index + 1
          }));

          const token = await getToken();
          if (!token) {
            setError('Authentication failed. Please log in again.');
            return;
          }

          const userId = localStorage.getItem('clerk-user-id') || '';

          const response = await fetch(`${API_URL}/prompts/global/order`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-clerk-user-id': userId
            },
            body: JSON.stringify({ promptOrders })
          });

          if (!response.ok) {
            throw new Error(`Failed to update prompt order: ${response.statusText}`);
          }

          const updatedPrompts = await response.json();
          const normalizedUpdatedPrompts: Prompt[] = (updatedPrompts as Prompt[]).map((prompt) => ({
            ...prompt,
            users: prompt.users ?? [],
          }));
          setPrompts(normalizedUpdatedPrompts);
          
          setSnackbarMessage('Prompt order updated successfully');
          setSnackbarOpen(true);
        } catch (error) {
          console.error('Error updating prompt order:', error);
          setSnackbarMessage('Failed to update prompt order');
          setSnackbarOpen(true);
          
          // Revert the local changes on error
          fetchPrompts();
        }
      }
    }
  };

  // Fetch global prompts
  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      if (!token) {
        setError('Authentication failed. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('Using token for fetch:', !!token); // Debug log
      
      const response = await fetch(`${API_URL}/prompts/global`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': localStorage.getItem('clerk-user-id') || '' // Add Clerk user ID from localStorage
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch prompts: ${response.statusText}`);
      }
      
      const data = await response.json();
      const normalizedPrompts: Prompt[] = (data as Prompt[]).map((prompt) => ({
        ...prompt,
        users: prompt.users ?? [],
      }));
      setPrompts(normalizedPrompts);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setError('Failed to load prompts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPromptAccessLists = async (promptId: string) => {
    try {
      setAccessDialogLoading(true);
      const token = await getToken();

      if (!token) {
        setSnackbarMessage('Authentication failed. Please log in again.');
        setSnackbarOpen(true);
        setAccessDialogLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/prompts/global/${promptId}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': localStorage.getItem('clerk-user-id') || '',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch prompt users: ${response.statusText}`);
      }

      const data = await response.json();
      const assigned = (data.assignedUsers ?? []) as PromptAccessUser[];
      const available = (data.availableUsers ?? []) as PromptAccessUser[];

      setAccessAssignedUsers(assigned);
      setAccessAvailableUsers(available);
      setAccessPrompt((prev) =>
        prev ? { ...prev, users: assigned } : prev
      );
      setPrompts((prevPrompts) =>
        prevPrompts.map((prompt) =>
          prompt.id === promptId ? { ...prompt, users: assigned } : prompt
        )
      );
    } catch (error) {
      console.error('Error fetching prompt access lists:', error);
      setSnackbarMessage('Failed to load prompt access details.');
      setSnackbarOpen(true);
    } finally {
      setAccessDialogLoading(false);
    }
  };

  const handleManagePromptAccess = (prompt: Prompt) => {
    setAccessPrompt(prompt);
    setAccessDialogOpen(true);
    fetchPromptAccessLists(prompt.id);
  };

  const handleCloseAccessDialog = () => {
    setAccessDialogOpen(false);
    setAccessPrompt(null);
    setAccessAssignedUsers([]);
    setAccessAvailableUsers([]);
    setAccessDialogLoading(false);
    setAccessMutationLoading(false);
  };

  const handleAddUserToPrompt = async (userId: string) => {
    if (!accessPrompt) {
      return;
    }

    const promptId = accessPrompt.id;

    try {
      setAccessMutationLoading(true);
      const token = await getToken();
      if (!token) {
        setSnackbarMessage('Authentication failed. Please log in again.');
        setSnackbarOpen(true);
        setAccessMutationLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/prompts/global/${accessPrompt.id}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': localStorage.getItem('clerk-user-id') || '',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to assign user: ${response.statusText}`);
      }

      const data = await response.json();
      const assigned = (data.assignedUsers ?? []) as PromptAccessUser[];
      const available = (data.availableUsers ?? []) as PromptAccessUser[];

      setAccessAssignedUsers(assigned);
      setAccessAvailableUsers(available);
      setAccessPrompt((prev) =>
        prev ? { ...prev, users: assigned } : prev
      );
      setPrompts((prevPrompts) =>
        prevPrompts.map((prompt) =>
          prompt.id === promptId ? { ...prompt, users: assigned } : prompt
        )
      );

      setSnackbarMessage('User added to prompt.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error adding user to prompt:', error);
      setSnackbarMessage('Failed to add user to prompt.');
      setSnackbarOpen(true);
    } finally {
      setAccessMutationLoading(false);
    }
  };

  const handleRemoveUserFromPrompt = async (userId: string) => {
    if (!accessPrompt) {
      return;
    }

    const promptId = accessPrompt.id;

    try {
      setAccessMutationLoading(true);
      const token = await getToken();
      if (!token) {
        setSnackbarMessage('Authentication failed. Please log in again.');
        setSnackbarOpen(true);
        setAccessMutationLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/prompts/global/${accessPrompt.id}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': localStorage.getItem('clerk-user-id') || '',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to remove user: ${response.statusText}`);
      }

      const data = await response.json();
      const assigned = (data.assignedUsers ?? []) as PromptAccessUser[];
      const available = (data.availableUsers ?? []) as PromptAccessUser[];

      setAccessAssignedUsers(assigned);
      setAccessAvailableUsers(available);
      setAccessPrompt((prev) =>
        prev ? { ...prev, users: assigned } : prev
      );
      setPrompts((prevPrompts) =>
        prevPrompts.map((prompt) =>
          prompt.id === promptId ? { ...prompt, users: assigned } : prompt
        )
      );

      setSnackbarMessage('User removed from prompt.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error removing user from prompt:', error);
      setSnackbarMessage('Failed to remove user from prompt.');
      setSnackbarOpen(true);
    } finally {
      setAccessMutationLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPrompts();
  }, [getToken]);
  
  // Cleanup effect to remove dragging class if component unmounts during drag
  useEffect(() => {
    return () => {
      document.body.classList.remove('dragging');
    };
  }, []);
  
  // Handle dialog open for new prompt
  const handleOpenDialog = () => {
    setEditingPrompt(null);
    setNewPromptTitle('');
    setNewPromptContent('');
    setNewPromptEnabled(true);
    setSelectedIconType('email');
    setDialogOpen(true);
  };
  
  // Handle dialog open for editing prompt
  const handleEditPrompt = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setNewPromptTitle(prompt.title);
    setNewPromptContent(prompt.promptContent);
    setNewPromptEnabled(prompt.enabled);
    setSelectedIconType(prompt.iconType || 'email');
    setDialogOpen(true);
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // Get icon component for displaying in the table
  const getIconComponent = (iconType: string) => {
    const icon = AVAILABLE_ICONS.find(icon => icon.value === iconType);
    return icon ? icon.icon : <FontAwesomeIcon icon={faEnvelope} />;
  };
  
  // Save prompt (create or update)
  const handleSavePrompt = async () => {
    if (!newPromptTitle.trim() || !newPromptContent.trim()) {
      setSnackbarMessage('Title and prompt content are required');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication failed. Please log in again.');
        return;
      }
      
      const userId = localStorage.getItem('clerk-user-id') || '';
      console.log('Debug: Using clerk-user-id for prompt save:', {
        hasUserId: !!userId,
        userId: userId.substring(0, 8) + '...' // Only log part of the ID for security
      });
      
      const promptData = {
        title: newPromptTitle,
        promptContent: newPromptContent,
        enabled: newPromptEnabled,
        isGlobal: true,
        iconType: selectedIconType
      };
      
      let url = `${API_URL}/prompts/global`;
      let method = 'POST';
      
      if (editingPrompt) {
        url = `${API_URL}/prompts/global/${editingPrompt.id}`;
        method = 'PUT';
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify(promptData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingPrompt ? 'update' : 'create'} prompt: ${response.statusText}`);
      }
      
      // Refresh prompts list
      const updatedResponse = await fetch(`${API_URL}/prompts/global`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        }
      });
      
      if (!updatedResponse.ok) {
        throw new Error('Failed to refresh prompts');
      }
      
      const updatedData = await updatedResponse.json();
      const normalizedUpdatedData: Prompt[] = (updatedData as Prompt[]).map((prompt) => ({
        ...prompt,
        users: prompt.users ?? [],
      }));
      setPrompts(normalizedUpdatedData);
      
      setSnackbarMessage(`Prompt ${editingPrompt ? 'updated' : 'created'} successfully`);
      setSnackbarOpen(true);
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving prompt:', error);
      setSnackbarMessage('Failed to save prompt');
      setSnackbarOpen(true);
    }
  };
  
  // Delete prompt
  const handleDeletePrompt = async (id: string) => {
    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication failed. Please log in again.');
        return;
      }
      
      const userId = localStorage.getItem('clerk-user-id') || '';
      
      const response = await fetch(`${API_URL}/prompts/global/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete prompt: ${response.statusText}`);
      }
      
      // Update local state
      setPrompts(prompts.filter(prompt => prompt.id !== id));
      
      setSnackbarMessage('Prompt deleted successfully');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setSnackbarMessage('Failed to delete prompt');
      setSnackbarOpen(true);
    }
  };
  
  // Handler for delete button click
  const handleDeletePromptClick = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setDeleteDialogOpen(true);
  };
  
  // Handle confirmation of deletion
  const confirmDelete = () => {
    if (selectedPrompt) {
      handleDeletePrompt(selectedPrompt.id);
      setDeleteDialogOpen(false);
    }
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  // Toggle prompt enabled status
  const handleToggleEnabled = async (prompt: Prompt) => {
    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication failed. Please log in again.');
        return;
      }
      
      const userId = localStorage.getItem('clerk-user-id') || '';
      
      const response = await fetch(`${API_URL}/prompts/global/${prompt.id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({ enabled: !prompt.enabled })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update prompt status: ${response.statusText}`);
      }
      
      // Update local state
      setPrompts(prompts.map(p => 
        p.id === prompt.id ? { ...p, enabled: !p.enabled } : p
      ));
      
      setSnackbarMessage(`Prompt ${!prompt.enabled ? 'enabled' : 'disabled'} successfully`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error toggling prompt status:', error);
      setSnackbarMessage('Failed to update prompt status');
      setSnackbarOpen(true);
    }
  };

  // Toggle expanded panel
  const handleToggleExpand = (promptId: string) => {
    setExpandedPromptId(expandedPromptId === promptId ? null : promptId);
  };

  // Open subprompt dialog for adding a new subprompt
  const handleOpenSubpromptDialog = (promptId: string) => {
    setEditingSubprompt(null);
    setNewSubpromptTitle('');
    setNewSubpromptContent('');
    setNewSubpromptEnabled(true);
    setSelectedSubpromptIconType('clipboard');
    setParentPromptId(promptId);
    setSubpromptDialogOpen(true);
  };

  // Open subprompt dialog for editing an existing subprompt
  const handleEditSubprompt = (subprompt: SubPrompt) => {
    setEditingSubprompt(subprompt);
    setNewSubpromptTitle(subprompt.title);
    setNewSubpromptContent(subprompt.promptContent);
    setNewSubpromptEnabled(subprompt.enabled);
    setSelectedSubpromptIconType(subprompt.iconType || 'clipboard');
    setParentPromptId(subprompt.globalPromptId);
    setSubpromptDialogOpen(true);
  };

  // Close subprompt dialog
  const handleCloseSubpromptDialog = () => {
    setSubpromptDialogOpen(false);
    setEditingSubprompt(null);
    setParentPromptId(null);
  };

  // Save subprompt
  const handleSaveSubprompt = async () => {
    if (!newSubpromptTitle.trim() || !newSubpromptContent.trim() || !parentPromptId) {
      setSnackbarMessage('Title, content, and parent prompt are required');
      setSnackbarOpen(true);
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication failed. Please log in again.');
        return;
      }

      const subpromptData = {
        title: newSubpromptTitle,
        promptContent: newSubpromptContent,
        enabled: newSubpromptEnabled,
        iconType: selectedSubpromptIconType
      };

      if (editingSubprompt) {
        // Update existing subprompt
        const result = await dispatch(updateSubPrompt({
          subPromptId: editingSubprompt.id,
          subPromptData: subpromptData
        })).unwrap();

        // Update local state
        setPrompts(prompts.map(prompt => {
          if (prompt.id === parentPromptId) {
            return {
              ...prompt,
              subPrompts: prompt.subPrompts?.map(sp => 
                sp.id === editingSubprompt.id ? result : sp
              )
            };
          }
          return prompt;
        }));

        setSnackbarMessage('Subprompt updated successfully');
      } else {
        // Create new subprompt
        const { subPrompt } = await dispatch(createSubPrompt({
          globalPromptId: parentPromptId,
          subPromptData: subpromptData
        })).unwrap();

        // Update local state
        setPrompts(prompts.map(prompt => {
          if (prompt.id === parentPromptId) {
            return {
              ...prompt,
              subPrompts: [...(prompt.subPrompts || []), subPrompt]
            };
          }
          return prompt;
        }));

        setSnackbarMessage('Subprompt created successfully');
      }

      setSnackbarOpen(true);
      handleCloseSubpromptDialog();
    } catch (error) {
      console.error('Error saving subprompt:', error);
      setSnackbarMessage('Failed to save subprompt');
      setSnackbarOpen(true);
    }
  };

  // Open delete subprompt dialog
  const handleDeleteSubpromptClick = (subprompt: SubPrompt) => {
    setSelectedSubprompt(subprompt);
    setDeleteSubpromptDialogOpen(true);
  };

  // Confirm delete subprompt
  const confirmDeleteSubprompt = async () => {
    if (!selectedSubprompt) return;

    try {
      await dispatch(deleteSubPromptAction({
        subPromptId: selectedSubprompt.id,
        globalPromptId: selectedSubprompt.globalPromptId
      })).unwrap();

      // Update local state
      setPrompts(prompts.map(prompt => {
        if (prompt.id === selectedSubprompt.globalPromptId) {
          return {
            ...prompt,
            subPrompts: prompt.subPrompts?.filter(sp => sp.id !== selectedSubprompt.id)
          };
        }
        return prompt;
      }));

      setSnackbarMessage('Subprompt deleted successfully');
      setSnackbarOpen(true);
      setDeleteSubpromptDialogOpen(false);
      setSelectedSubprompt(null);
    } catch (error) {
      console.error('Error deleting subprompt:', error);
      setSnackbarMessage('Failed to delete subprompt');
      setSnackbarOpen(true);
    }
  };

  // Toggle subprompt enabled status
  const handleToggleSubpromptEnabled = async (subprompt: SubPrompt) => {
    try {
      const result = await dispatch(toggleSubPromptEnabled({
        subPromptId: subprompt.id,
        enabled: !subprompt.enabled
      })).unwrap();

      // Update local state
      setPrompts(prompts.map(prompt => {
        if (prompt.id === subprompt.globalPromptId) {
          return {
            ...prompt,
            subPrompts: prompt.subPrompts?.map(sp => 
              sp.id === subprompt.id ? result : sp
            )
          };
        }
        return prompt;
      }));

      setSnackbarMessage(`Subprompt ${!subprompt.enabled ? 'enabled' : 'disabled'} successfully`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error toggling subprompt status:', error);
      setSnackbarMessage('Failed to update subprompt status');
      setSnackbarOpen(true);
    }
  };

  return (
    <Box>
      {/* Global styles for better mobile drag behavior */}
      <style>{`
        [data-dnd-kit-drag-overlay] {
          z-index: 999 !important;
        }
        
        .dnd-mobile-handle {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -khtml-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          touch-action: none;
        }
        
        @media (max-width: 768px) {
          body.dragging {
            overflow: hidden;
          }
        }
      `}</style>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Prompt Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add New Prompt
        </Button>
      </Box>

      {prompts.length > 1 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> You can reorder prompts by {isMobile ? 'touching and holding the gray drag handle' : 'clicking and dragging the drag handle'} 
            ({<DragIcon fontSize="small" sx={{ mx: 0.5, verticalAlign: 'middle' }} />}) {isMobile ? 'and then moving up or down' : 'to reorder them'}. 
            The order you set here will be reflected in the transcription interface.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : prompts.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No prompts available. Create one to get started.
          </Typography>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={prompts.map(p => p.id)} strategy={verticalListSortingStrategy}>
            {isMobile ? (
              // Mobile card view with drag and drop
              <Grid container spacing={2}>
                {prompts.map((prompt) => (
                  <Grid item xs={12} key={prompt.id}>
                    <SortablePromptItem
                      prompt={prompt}
                      expandedPromptId={expandedPromptId}
                      onToggleExpand={handleToggleExpand}
                      onEditPrompt={handleEditPrompt}
                      onDeletePrompt={handleDeletePromptClick}
                      onToggleEnabled={handleToggleEnabled}
                      onOpenSubpromptDialog={handleOpenSubpromptDialog}
                    onEditSubprompt={handleEditSubprompt}
                    onDeleteSubprompt={handleDeleteSubpromptClick}
                    onToggleSubpromptEnabled={handleToggleSubpromptEnabled}
                    getIconComponent={getIconComponent}
                    onManageAccess={handleManagePromptAccess}
                    isMobile={true}
                  />
                  </Grid>
                ))}
              </Grid>
            ) : (
              // Desktop table view with drag and drop
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell width={50}></TableCell>
                      <TableCell width={50}>Order</TableCell>
                      <TableCell>Icon</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Users</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prompts.map((prompt) => (
                      <SortablePromptItem
                        key={prompt.id}
                        prompt={prompt}
                        expandedPromptId={expandedPromptId}
                        onToggleExpand={handleToggleExpand}
                        onEditPrompt={handleEditPrompt}
                        onDeletePrompt={handleDeletePromptClick}
                        onToggleEnabled={handleToggleEnabled}
                        onOpenSubpromptDialog={handleOpenSubpromptDialog}
                        onEditSubprompt={handleEditSubprompt}
                    onDeleteSubprompt={handleDeleteSubpromptClick}
                    onToggleSubpromptEnabled={handleToggleSubpromptEnabled}
                    getIconComponent={getIconComponent}
                    onManageAccess={handleManagePromptAccess}
                    isMobile={false}
                  />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: isMobile ? '280px' : '400px',
                  transform: 'rotate(5deg)'
                }}
              >
                <DragIcon sx={{ color: 'rgba(0,0,0,0.6)' }} />
                {(() => {
                  const activePrompt = prompts.find(p => p.id === activeId);
                  return activePrompt ? (
                    <>
                      <Box sx={{ minWidth: 30 }}>
                        {getIconComponent(activePrompt.iconType)}
                      </Box>
                      <Typography variant="h6" sx={{ flex: 1 }}>
                        {activePrompt.title}
                      </Typography>
                    </>
                  ) : null;
                })()}
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Prompt Dialog (Add/Edit) */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingPrompt ? "Edit Prompt" : "Add New Prompt"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Prompt Title"
                variant="outlined"
                fullWidth
                value={newPromptTitle}
                onChange={(e) => setNewPromptTitle(e.target.value)}
                required
                margin="normal"
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="icon-select-label">Icon</InputLabel>
                <Select
                  labelId="icon-select-label"
                  value={selectedIconType}
                  label="Icon"
                  onChange={(e) => setSelectedIconType(e.target.value)}
                >
                  {AVAILABLE_ICONS.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>{icon.icon}</Box>
                        <Typography>{icon.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={newPromptEnabled}
                    onChange={(e) => setNewPromptEnabled(e.target.checked)}
                  />
                }
                label="Enabled"
                sx={{ mt: 2, display: 'block' }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Prompt Content"
                variant="outlined"
                fullWidth
                multiline
                rows={isMobile ? 6 : 10}
                value={newPromptContent}
                onChange={(e) => setNewPromptContent(e.target.value)}
                required
                margin="normal"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {"Use {{transcription}} placeholder to include the user's transcription content in your prompt."}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePrompt}
            variant="contained"
            color="primary"
            disabled={loading || !newPromptTitle || !newPromptContent}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt Access Dialog */}
      <Dialog
        open={accessDialogOpen}
        onClose={handleCloseAccessDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Manage Access
          {accessPrompt ? ` — ${accessPrompt.title}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {accessDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Members with access
                </Typography>
                {accessAssignedUsers.length > 0 ? (
                  <Stack spacing={1}>
                    {accessAssignedUsers.map((user) => (
                      <Box
                        key={user.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          p: 1,
                          border: '1px solid rgba(0,0,0,0.06)',
                          borderRadius: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {getUserDisplayName(user)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                            {user.isDeactivated ? ' (Inactive)' : ''}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveUserFromPrompt(user.id)}
                          disabled={accessMutationLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No users currently have access.
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Add members
                </Typography>
                {accessAvailableUsers.length > 0 ? (
                  <Stack spacing={1}>
                    {accessAvailableUsers.map((user) => (
                      <Button
                        key={user.id}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => handleAddUserToPrompt(user.id)}
                        disabled={accessMutationLoading}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                        }}
                        fullWidth
                      >
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {getUserDisplayName(user)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    All users already have access to this prompt.
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAccessDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Subprompt Dialog (Add/Edit) */}
      <Dialog
        open={subpromptDialogOpen}
        onClose={handleCloseSubpromptDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingSubprompt ? "Edit Sub-prompt" : "Add New Sub-prompt"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Sub-prompt Title"
                variant="outlined"
                fullWidth
                value={newSubpromptTitle}
                onChange={(e) => setNewSubpromptTitle(e.target.value)}
                required
                margin="normal"
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="subprompt-icon-select-label">Icon</InputLabel>
                <Select
                  labelId="subprompt-icon-select-label"
                  value={selectedSubpromptIconType}
                  label="Icon"
                  onChange={(e) => setSelectedSubpromptIconType(e.target.value)}
                >
                  {AVAILABLE_ICONS.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>{icon.icon}</Box>
                        <Typography>{icon.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={newSubpromptEnabled}
                    onChange={(e) => setNewSubpromptEnabled(e.target.checked)}
                  />
                }
                label="Enabled"
                sx={{ mt: 2, display: 'block' }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Sub-prompt Content"
                variant="outlined"
                fullWidth
                multiline
                rows={isMobile ? 6 : 10}
                value={newSubpromptContent}
                onChange={(e) => setNewSubpromptContent(e.target.value)}
                required
                margin="normal"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {"Use {{transcription}} placeholder to include the user's transcription content in your prompt."}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSubpromptDialog}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSubprompt}
            variant="contained"
            color="primary"
            disabled={loading || !newSubpromptTitle || !newSubpromptContent}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog for subprompts */}
      <Dialog
        open={deleteSubpromptDialogOpen}
        onClose={() => setDeleteSubpromptDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this sub-prompt? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSubpromptDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteSubprompt} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this prompt? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default PromptManagement; 

